// Modules
import { cloneElement, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Types
import type { MouseEvent, RefObject } from 'react';
import type { TDropdownOpenState, TDropdownProps, TDropdownStyle } from './Dropdown.type';

// Components
import DropdownDialogue from './DropdownDialogue';

// Helpers
import { getDropdownPosition } from './Dropdown.helper';

// Constants
import {
	DROPDOWN_DEFAULT_OPEN_STATE,
	DROPDOWN_DEFAULT_PROPS, DROPDOWN_DEFAULT_STYLE,
	DROPDOWN_PADDING_CLOSE,
	DROPDOWN_PADDING_OPEN,
	DROPDOWN_TIMEOUT
} from './Dropdown.constant';

const Dropdown = (props: TDropdownProps) => {
	const {
		children,
		className,
		position = DROPDOWN_DEFAULT_PROPS.position,
		ref,
		style,
		trigger,
		...restProps
	} = props
	const localRef = useRef<HTMLDivElement | null>(null);
	const dropdownRef = ref as RefObject<HTMLDivElement> || localRef;
	const triggerRef = useRef<HTMLDivElement | null>(null);

	const [openState, setOpenState] = useState<TDropdownOpenState>(DROPDOWN_DEFAULT_OPEN_STATE);
	const [dropdownStyle, setDropdownStyle] = useState<TDropdownStyle>(DROPDOWN_DEFAULT_STYLE);
	const { isOpen, isShow } = openState;
	const { height, left, top } = dropdownStyle;
	const dropdownHeight = isOpen ? height : 0;
	const dropdownPadding = isOpen ? DROPDOWN_PADDING_OPEN : DROPDOWN_PADDING_CLOSE;

	const clonedTarget = useMemo(() => cloneElement(trigger!, {
		...trigger.props,
		ref: triggerRef,
		onClick: (e: MouseEvent) => {
			trigger.props.onClick?.(e)
			if (!isOpen) {
				setOpenState(prevState => ({ ...prevState, isShow: true }))
			} else {
				setOpenState({ ...openState, isOpen: false })
			}
		},
		style: {
			...trigger.props.style,
			cursor: 'pointer',
		}
	}), [openState.isOpen])

	useEffect(() => {
		setOpenState({ isOpen: false, isShow: false })
	}, [])

	useEffect(() => {
		if (
			(triggerRef && triggerRef?.current)
			&& (dropdownRef && dropdownRef?.current)
		) {
			const triggerRect = triggerRef?.current!.getBoundingClientRect()
			const dropdownRect = dropdownRef?.current!.getBoundingClientRect()
			if (dropdownRect.height !== 0 && dropdownRect.width !== 0) {
				const { top, left } = getDropdownPosition({
					position: position!,
					triggerHeight: triggerRect.height || 0,
					triggerWidth: triggerRect.width || 0,
					triggerTop: triggerRect.top || 0,
					triggerLeft: triggerRect.left || 0,
					dropdownHeight: dropdownRect.height || 0,
					dropdownWidth: dropdownRect.width || 0
				})

				setDropdownStyle({ top, left, height: dropdownRect.height })
			}
		}
	}, [position])

	useEffect(() => {
		const handleBlurSelect = (e: Event) => {
			const target = e.target as HTMLElement;

			if (
				(!!triggerRef?.current && !triggerRef?.current!.contains(target))
				&& (!!dropdownRef?.current && !dropdownRef?.current!.contains(target))
			) {
				setOpenState({ ...openState, isOpen: false })
			}
		}

		const disposeOption = setTimeout(() => {
			setOpenState(prevState => ({ ...prevState, isShow: false }));
		}, DROPDOWN_TIMEOUT)

		if (isOpen) {
			clearTimeout(disposeOption)
			document.addEventListener('click', handleBlurSelect);
		} else {
			document.removeEventListener('click', handleBlurSelect);
		}

		return () => {
			clearTimeout(disposeOption)
			document.removeEventListener('click', handleBlurSelect);
		}
	}, [isOpen]);

	useEffect(() => {
		if (isShow) {
			setOpenState(prevState => ({ ...prevState, isOpen: true }))
		}
	}, [isShow])

	return (
		<>
			{createPortal(
				isShow && (
					<DropdownDialogue
						ref={dropdownRef}
						className={className}
						style={{
							...style,
							position: 'absolute',
							top,
							left,
							height: dropdownHeight,
							padding: dropdownPadding,
						}}
						{...restProps}
					>
						{children}
					</DropdownDialogue>
				),
				document.body
			)}
			{clonedTarget}
		</>
	)
}

export default Dropdown
